import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label, Button } from 'reactstrap';
import { VpSelect } from '../ui/VpSelect';

export interface ReassignOperator { user_id: number; email: string | null; label: string; }

export interface ReassignMissionDialogProps {
  isOpen: boolean;
  missionId: number;
  missionLabel: string;
  /** Mission is active (operator already picking) → show interruption warning. */
  isActive: boolean;
  /** Current operator — excluded from the picker (reassigning to the same op is a no-op). */
  currentOperatorUserId: number | null;
  operators: ReassignOperator[];
  pending?: boolean;
  error?: string | null;
  /** 'assign' = bind an operator to a pending mission; 'reassign' = default. */
  mode?: 'reassign' | 'assign';
  onSubmit: (vars: { newOperatorUserId: number }) => void;
  onCancel: () => void;
}

export function ReassignMissionDialog({
  isOpen, missionId, missionLabel, isActive, currentOperatorUserId, operators,
  pending, error, mode = 'reassign', onSubmit, onCancel,
}: ReassignMissionDialogProps) {
  const { t } = useTranslation('picklist');
  const isAssign = mode === 'assign';
  const [newOp, setNewOp] = useState<number | null>(null);

  useEffect(() => {
    if (isOpen) { setNewOp(null); }
  }, [isOpen, missionId]);

  if (!isOpen) return null;

  const options = operators
    .filter((o) => o.user_id !== currentOperatorUserId)
    .map((o) => ({ value: o.user_id, label: o.label || o.email || t('reassign_dialog.operator_default_label', { id: o.user_id }) }));

  const canSubmit = newOp !== null && !pending;
  const titleKey = isAssign ? 'reassign_dialog.title_assign' : 'reassign_dialog.title_reassign';
  const ariaKey = isAssign ? 'reassign_dialog.aria_label_assign' : 'reassign_dialog.aria_label_reassign';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t(ariaKey, { label: missionLabel })}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--vp-bg)', padding: 16, borderRadius: 'var(--vp-r-md)', minWidth: 420, maxWidth: 560, boxShadow: 'var(--vp-shadow-lg)' }}
      >
        <h5 className="fs-15 mb-2">{t(titleKey, { label: missionLabel })}</h5>
        {isActive && !isAssign && (
          <div role="alert" className="alert alert-warning py-2 px-3 small mb-3">
            {t('reassign_dialog.active_warning')}
          </div>
        )}

        <FormGroup className="mb-2">
          <Label className="mb-1">{isAssign ? t('reassign_dialog.operator_label_assign') : t('reassign_dialog.operator_label_new')}</Label>
          <VpSelect
            ariaLabel={t('reassign_dialog.operator_aria')}
            placeholder={t('reassign_dialog.operator_placeholder')}
            options={options}
            value={newOp}
            onChange={(v) => setNewOp(v === null ? null : Number(v))}
          />
        </FormGroup>

        {error && <p role="alert" className="text-danger small mb-0">{error}</p>}

        <div className="d-flex gap-2 justify-content-end mt-3">
          <Button type="button" color="light" onClick={onCancel} disabled={pending}>{t('reassign_dialog.cancel')}</Button>
          <Button type="button" color="primary"
                  disabled={!canSubmit}
                  onClick={() => { if (newOp !== null) onSubmit({ newOperatorUserId: newOp }); }}>
            {pending
              ? (isAssign ? t('reassign_dialog.submitting_assign') : t('reassign_dialog.submitting_reassign'))
              : (isAssign ? t('reassign_dialog.submit_assign') : t('reassign_dialog.submit_reassign'))}
          </Button>
        </div>
      </div>
    </div>
  );
}
